import { useAtom } from "jotai";
import { backendDataAtom } from "atoms/backendDataAtom.js";
import { backendPost, backendGet, backendPatch } from "@accredo/fe-shared/services/dataService.js";
import { useSnackbar } from "notistack";
import {
  setRelationState,
  updateRelationState,
  updateRelationStateMany,
} from "./claimRelationStateHelpers.js";

import { useClaimActions } from "./useClaimActions.jsx";

import { useRef } from "react";

export function useClaimAttachmentActions() {
  const [backendData, setBackendData] = useAtom(backendDataAtom);
  const { enqueueSnackbar } = useSnackbar();

  const abortControllerRef = useRef(null);

  const relationKey = "attachments";
  const state = backendData?.relations?.[relationKey];

  const setState = (data) =>
    setRelationState(backendData, setBackendData, relationKey, data);
  const updateStateItem = (data) =>
    updateRelationState(backendData, setBackendData, relationKey, data);
  const updateStateItems = (items) =>
    updateRelationStateMany(backendData, setBackendData, relationKey, items);

  const { pushRelationItem: pushClaimRelation } = useClaimActions();

  const findById = (id, options) => {
    const {
      label = "Anlage",
      setIsLoading = () => {},
      setResult = () => {},
    } = options || {};

    abortControllerRef.current = new AbortController();

    return backendGet(`data/anlages/${id}`, {
      label,
      setIsLoading,
      setResult,
      enqueueSnackbar,
      options: {
        params: {
          include: ["filedata"],
        },
      },
      signal: abortControllerRef.current.signal,
    });
  };

  const createClaimAttachments = async (data, options = {}) => {
    const { label = "Anlage erstellen", setIsLoading = () => {} } = options;

    abortControllerRef.current = new AbortController();

    await backendPost("data/anlage", {
      label,
      setIsLoading,
      toSend: data,
      setResult: (result) => {
        const attachments = Array.isArray(result) ? result : [result];
        updateStateItems(attachments);
        attachments.forEach((attachment) => {
          pushClaimRelation("anlagen", attachment);
        });
      },
      enqueueSnackbar,
      signal: abortControllerRef.current.signal,
      ...options,
    });
  };

  const abort = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
  };

  const updateClaimAttachment = async (data, options) => {
    const {
      label = "Anlage aktualisieren",
      setIsLoading = () => {},
      ...restOptions
    } = options || {};

    const attachment_id = data?.id;
    if (!attachment_id) {
      return;
    }

    abortControllerRef.current = new AbortController();

    await backendPatch(`data/anlages/${attachment_id}`, {
      label,
      setIsLoading,
      toSend: {
        ...(data?.bezeichnung !== undefined ? { bezeichnung: data.bezeichnung } : {}),
        ...(data?.used_in !== undefined ? { used_in: data.used_in } : {}),
      },
      setResult: (result) => {
        updateStateItem(result);
      },
      enqueueSnackbar,
      signal: abortControllerRef.current.signal,
      ...restOptions,
    });
  };

  const toggleClaimAttachmentInternal = async (
    attachment,
    internal,
    options = {}
  ) => {
    await backendPatch(`data/anlages/${attachment.id}/toggle-internal`, {
      toSend: {
        internal,
      },
      enqueueSnackbar,
      setResult: (data) => {
        updateStateItem(data);
      },
      ...options,
    });
  };

  return {
    findById,
    createClaimAttachments,
    updateClaimAttachment,
    toggleClaimAttachmentInternal,
    setState,
    state,
    abort,
  };
}
